import { Component, Input, OnInit } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { GenericModalService } from '../../../../services/generic-modal.service';
import { Utils } from '../../../../services/utils';
import { CustomValidator } from '../../../../services/custom-validator';
import { DateTimeService } from '../../../../services/date-time.service';
import { ExtractionService } from '../../../../services/configuration/extraction.service';
import { BusinessProcessService } from '../../../../services/business-process.service';

@Component({
  selector: 'app-scheduling-modal',
  templateUrl: './extraction-scheduling-modal.component.html',
  styleUrls: ['./extraction-scheduling-modal.component.css', '../../../../styles/modals.css']
})
export class ExtractionSchedulingModalComponent implements OnInit {

  @Input() modalTitle: string;
  itemSaved: Subject<boolean> = new Subject();
  scheduleForm: FormGroup;
  objInitialState: any;
  submitted = false;
  submitClick = false;
  errorMessage = '';
  successMessage = '';
  isNew: boolean;
  fileLayoutsList$: Observable<any[]>;
  filteredRepetitions = [];
  hasRight: boolean;
  hasRight$: Observable<any[]>;
  repetitions = [
    { value: 0, name: 'Não Repetir' },
    { value: 600, name: '10 minutos' },
    { value: 900, name: '15 minutos' },
    { value: 1800, name: '30 minutos' },
    { value: 3600, name: '1 hora' }
  ];
  durations = [
    { value: 1800, name: '30 minutos' },
    { value: 3600, name: '1 hora' },
    { value: 43200, name: '12 horas' },
    { value: 86400, name: '1 dia' }
  ];
  types = [
    { value: 1, name: 'Fixo' },
    { value: 2, name: 'Layout de arquivo' }
  ];
  intervalTypes = [
    { value: 1, name: 'Período Fixo' },
    { value: 2, name: 'Incremental' },
    { value: 3, name: 'Retroativo' }
  ];
  retroactiveIntervalTypes = [
    { value: 1, name: 'Hora(s)' },
    { value: 2, name: 'Dia(s)' },
    { value: 3, name: 'Mês(es)' }
  ];

  get formData() { return this.scheduleForm.controls; }
  get dataExtractionDefinitionData() { return this.formData.dataExtractionDefinition['controls']; }
  get schedulingInfoData() { return this.formData.schedulingInfo['controls']; }

  constructor(
    public bsModalRef: BsModalRef,
    private modalService: GenericModalService,
    private formBuilder: FormBuilder,
    private dateTimeService: DateTimeService,
    private extractionService: ExtractionService,
    private businessProcessService: BusinessProcessService
  ) { }

  ngOnInit() {
    this.checkRights();
    this.hasRight$.subscribe( rights => {
      this.hasRight = rights.length > 0;
      if (this.hasRight) {
        this.isNew = this.objInitialState.name === undefined;
        this.createForm();
        this.getFileLayoutsForDataExtraction();
        if (!this.isNew) {
          this.patchValues();
        }
      } else {
        this.errorMessage = 'Usuário não tem direito de efetuar esta ação.';
      }
    }, error1 => {
      this.errorMessage = error1;
    });

  }

  createForm() {
    this.scheduleForm = this.formBuilder.group({
      dataExtractionDefinition: this.formBuilder.group({
        dataExtractionDefinitionId: 0,
        name: ['', [Validators.required, Validators.pattern('^[^<>:\\"\\\\\\/|?*[\\]{}\\$%¨&\\(\\)\\-\\+§`ªº\\-°\'\\^~¢¬£³²¹]+$')] ],
        description: '',
        type: [1, Validators.required],
        intervalType: 1,
        intervalStart: [ this.getToday(), Validators.required ],
        intervalEnd: [ this.getToday(), Validators.required ],
        intervalTimeAmount: [ 1, [Validators.min(1), Validators.max(999), CustomValidator.numeric] ],
        retroactiveIntervalType: 1,
        fileLayout: null,
        isDeleted: false,
        onlyLastLifeCycle: false
      }),
      schedulingInfo: this.formBuilder.group({
        repetition: 0,
        dailyFrequency: [ 1, [Validators.min(1), Validators.max(999), CustomValidator.numeric] ],
        start: [ this.getToday(), Validators.required ],
        enabled: true,
        repetitionDuration: 0,
        schedulingId: 0,
        taskId: 0,
        taskType: 0
      })
    });
    this.scheduleForm.controls['dataExtractionDefinition'].setValidators(CustomValidator.validateDatetime('intervalStart', 'intervalEnd'));
  }

  patchValues() {
    delete this.objInitialState.extensionData;
    this.getFilteredRepetitionDurations(this.objInitialState.schedulingInfo.repetition);
    this.scheduleForm.patchValue(
      {
        dataExtractionDefinition: {
          dataExtractionDefinitionId: this.objInitialState.dataExtractionDefinition.dataExtractionDefinitionId,
          name: this.objInitialState.dataExtractionDefinition.name,
          description: this.objInitialState.dataExtractionDefinition.description,
          type: this.objInitialState.dataExtractionDefinition.type,
          intervalType: this.objInitialState.dataExtractionDefinition.intervalType,
          fileLayout: this.objInitialState.dataExtractionDefinition.fileLayout ?
                      this.objInitialState.dataExtractionDefinition.fileLayout.fileLayoutId : null,
          intervalStart: this.objInitialState.dataExtractionDefinition.intervalStart ?
                  Utils.formatDate(this.objInitialState.dataExtractionDefinition.intervalStart, 'yyyy-MM-DDTHH:mm:ss') : null,
          intervalEnd: this.objInitialState.dataExtractionDefinition.intervalEnd ?
                  Utils.formatDate(this.objInitialState.dataExtractionDefinition.intervalEnd, 'yyyy-MM-DDTHH:mm:ss') : null,
          intervalTimeAmount: this.objInitialState.dataExtractionDefinition.intervalTimeAmount,
          retroactiveIntervalType: this.objInitialState.dataExtractionDefinition.retroactiveIntervalType,
          isDeleted: this.objInitialState.dataExtractionDefinition.isDeleted,
          onlyLastLifeCycle: this.objInitialState.dataExtractionDefinition.onlyLastLifeCycle
        },
        schedulingInfo: {
          repetition: this.objInitialState.schedulingInfo.repetition,
          dailyFrequency: this.objInitialState.schedulingInfo.dailyFrequency,
          start: this.objInitialState.schedulingInfo.start ?
                  Utils.formatDate(this.objInitialState.schedulingInfo.start, 'yyyy-MM-DDTHH:mm:ss') : null,
          enabled: this.objInitialState.schedulingInfo.enabled,
          repetitionDuration: Number(this.objInitialState.schedulingInfo.repetitionDuration),
          schedulingId: this.objInitialState.schedulingInfo.schedulingId,
          taskId: this.objInitialState.schedulingInfo.taskId,
          taskType: this.objInitialState.schedulingInfo.taskType
        }
      }
    );
    this.onChangeIntervalType(this.objInitialState.dataExtractionDefinition.intervalType.toString());
  }

  getFilteredRepetitionDurations(definition) {
    if (definition === undefined) {
      return [];
    } else {
      const durationsConst = Array.from(this.durations);
      for (let i = durationsConst.length - 1; i >= 0; i--) {
        if (durationsConst[i].value <= definition) {
          durationsConst.splice(i, 1);
        }
      }
      this.filteredRepetitions = durationsConst;
    }
  }

  getFileLayoutsForDataExtraction() {
    this.fileLayoutsList$ = this.extractionService.getFileLayoutsForDataExtraction();
  }

  onChangeType(type) {
    if (type == 2) {
      this.dataExtractionDefinitionData['fileLayout'].setValidators(Validators.required);
    } else {
      this.dataExtractionDefinitionData['fileLayout'].clearValidators();
      this.scheduleForm.controls.dataExtractionDefinition.patchValue(
        { fileLayout: null,  onlyLastLifeCycle: null }
      );
    }
    this.dataExtractionDefinitionData['fileLayout'].updateValueAndValidity();
  }

  onChangeIntervalType(intervalType) {
    const addValidatorsControls = [];
    const removeValidatorsControls = [];
    switch (intervalType) {
      case '1': addValidatorsControls.push('intervalStart', 'intervalEnd');
                removeValidatorsControls.push('intervalTimeAmount', 'retroactiveIntervalType');
                this.scheduleForm.controls.dataExtractionDefinition.setValidators(CustomValidator.validateDatetime('intervalStart', 'intervalEnd'));
                this.scheduleForm.controls.dataExtractionDefinition.patchValue(
                  { intervalTimeAmount: null, retroactiveIntervalType: 1 }
                );
        break;
      case '2': addValidatorsControls.push('intervalStart');
                removeValidatorsControls.push('dataExtractionDefinition', 'intervalEnd', 'intervalTimeAmount', 'retroactiveIntervalType');
                this.scheduleForm.controls.dataExtractionDefinition.patchValue(
                  { intervalTimeAmount: null, retroactiveIntervalType: 1 }
                );
        break;
      case '3': addValidatorsControls.push('intervalTimeAmount', 'retroactiveIntervalType');
                removeValidatorsControls.push('dataExtractionDefinition', 'intervalStart', 'intervalEnd');
    }

    addValidatorsControls.forEach( control => {
      if (control == 'intervalTimeAmount') {
        this.dataExtractionDefinitionData['intervalTimeAmount'].setValidators([
          Validators.min(1), Validators.max(999), CustomValidator.numeric ]);
      } else {
        this.dataExtractionDefinitionData[control].setValidators(Validators.required);
      }
      this.dataExtractionDefinitionData[control].updateValueAndValidity();
    });

    removeValidatorsControls.forEach( control => {
      if (control == 'dataExtractionDefinition') {
        this.scheduleForm.controls['dataExtractionDefinition'].clearValidators();
      } else {
        this.dataExtractionDefinitionData[control].clearValidators();
        this.dataExtractionDefinitionData[control].updateValueAndValidity();
      }
    });
  }

  onChangeRepetition(repetition) {
    this.getFilteredRepetitionDurations(repetition);
    if (repetition > 0) {
      this.scheduleForm.controls.schedulingInfo.patchValue(
        { repetitionDuration: this.filteredRepetitions[0].value }
      );
    }
  }

  onClose() {
    this.itemSaved.next(false);
    this.bsModalRef.hide();
  }

  onSave() {
    const schedule = this.scheduleForm.getRawValue();
    schedule.dataExtractionDefinition.intervalType = Number(schedule.dataExtractionDefinition.intervalType);
    schedule.schedulingInfo.start = Utils.toUTCThenFormat(schedule.schedulingInfo.start);
    switch (schedule.dataExtractionDefinition.intervalType) {
      case 1: schedule.dataExtractionDefinition.IntervalTimeAmount = null;
              schedule.dataExtractionDefinition.intervalStart
                = Utils.toUTCThenFormat(schedule.dataExtractionDefinition.intervalStart);
              schedule.dataExtractionDefinition.intervalEnd
                = Utils.toUTCThenFormat(schedule.dataExtractionDefinition.intervalEnd);
        break;
      case 2: schedule.dataExtractionDefinition.intervalEnd = null;
              schedule.dataExtractionDefinition.intervalStart
                = Utils.toUTCThenFormat(schedule.dataExtractionDefinition.intervalStart);
              schedule.dataExtractionDefinition.intervalTimeAmount = null;
        break;
    }
    schedule.dataExtractionDefinition.retroactiveIntervalType = Number(schedule.dataExtractionDefinition.retroactiveIntervalType);
    schedule.dataExtractionDefinition.type = Number(schedule.dataExtractionDefinition.type);
    if (schedule.dataExtractionDefinition.type === 2) {
      schedule.dataExtractionDefinition.fileLayout = { fileLayoutId: schedule.dataExtractionDefinition.fileLayout };
    } else {
      schedule.dataExtractionDefinition.fileLayout = null;
    }
    if (this.isNew) {
      this.createDataExtractionScheduling(schedule);
    } else {
      this.updateDataExtractionScheduling(schedule);
    }
  }

  createDataExtractionScheduling(schedule) {
    this.errorMessage = '';
    this.successMessage = '';
    this.extractionService.createSchedulingWithDataExtractionDef(schedule)
      .subscribe( result => {
        this.submitted = true;
        this.itemSaved.next(true);
        this.successMessage = 'Agendamento criado com sucesso';
        setTimeout(() => { this.submitted = false; this.bsModalRef.hide(); }, 2000);
      }, error1 => {
        let err = error1;
        if (error1.indexOf('401') !== -1) {
          err = 'Erro: Sessão expirada.';
        }
        this.errorMessage = err;
        this.submitClick = false;
      });
  }

  updateDataExtractionScheduling(schedule) {
    this.successMessage = '';
    this.errorMessage = '';
    this.extractionService.updateSchedulingWithDataExtractionDef(schedule)
      .subscribe( () => {
        this.submitted = true;
        this.itemSaved.next(true);
        this.successMessage = 'Agendamento alterado com sucesso';
        setTimeout(() => { this.submitted = false; this.bsModalRef.hide(); }, 2000);
      }, error1 => {
        let err = error1;
        if (error1.indexOf('401') !== -1) {
          err = 'Erro: Sessão expirada.';
        }
        this.errorMessage = err;
        this.submitClick = false;
      });
  }

  getToday() {
    const date = new Date(Date.now()).toISOString()
      .substr(0, 19);
    return this.dateTimeService.toLocalISOStringDateTime(date);
  }

  adjustInitialDate(value) {
    const now = this.getToday();
    if (value > now) {
      this.scheduleForm.controls.dataExtractionDefinition.patchValue(
        { intervalStart: now }
      );
    }
  }

  adjustSchedulingStart(value) {
    const now = this.getToday();
    if (value < now) {
      this.scheduleForm.controls.schedulingInfo.patchValue(
        { start: now }
      );
    }
  }

  adjustSpaces(value, formControlName) {
    Utils.adjustSpaces(value, this.scheduleForm.controls['dataExtractionDefinition'] as FormGroup, formControlName);
  }

  blockUserInput() {
    return false;
  }

  checkRights() {
    this.errorMessage = '';
    this.hasRight$ = this.businessProcessService.checkRights(2097152);
  }

}
